import {
  CurrencyCircleDollar,
  Gauge,
  Gear,
  House,
  SquaresFour,
  Storefront,
  Tag,
  Truck,
  UserCircle,
  Users,
} from '@phosphor-icons/react';
import { routes } from '../../../routes/routes';

/**
 * The nav model both tracks read from.
 *
 * One list per shell, shared by the desktop chrome and the mobile chrome, which
 * is the whole point of holding it here: the tab bar, the top nav links, the
 * sidebar groups and the mobile drawer are four presentations of the SAME
 * information architecture, and they must not be allowed to drift apart.
 *
 * Each item carries BOTH icon sources because the two tracks draw icons
 * differently and neither may borrow from the other:
 *
 *   `icon`  a Phosphor component, rendered by the desktop track via <Icon as>
 *   `glyph` an id into glyphs.jsx, rendered by the mobile track as inline SVG
 *
 * `match` is an optional prefix used for active detection when the route the
 * item points at is not the only route the section owns.
 */

/**
 * MarketShell: the buying nav.
 *
 * GAMES, NOT SETS. `/sets` does not list sets, it lists trading card GAMES —
 * Pokemon, One Piece, Magic — and you pick a game before you ever see a set.
 * The sets themselves live one level down at `/sets/category/:tcgId`. The
 * fixture and the view have called it "the games index" all along; only the
 * label said Sets, which put the wrong word on the tab AND on the page.
 *
 * ORDERS IS NOT HERE. See the note beside the list.
 *
 * Wherever orders ARE linked, they go to the dashboard's order-history tab and
 * never to `/order-tracking`. That route is a track-by-reference LOOKUP tool —
 * you land on an empty field asking for a tracking ID — and pointing anything
 * called "Orders" at it was the biggest routing mix-up in the nav. Order
 * tracking stays a real route and stays in the ADMIN nav, which is the console
 * that actually wants it.
 */
export const MARKET_NAV = [
  {
    id: 'marketplace',
    label: 'Marketplace',
    to: routes.MARKETPLACE,
    match: '/marketplace',
    /* `App.js` mounts the same <Marketplace /> at `/`, and the lockup goes
       there, so the front door has to light this item or the click that took
       you home puts the nav out. See `alsoAt` on `isNavItemActive`. */
    alsoAt: [routes.HOME],
    icon: Storefront,
    glyph: 'storefront',
  },
  {
    id: 'sets',
    label: 'Games',
    to: routes.SETS,
    match: '/sets',
    icon: SquaresFour,
    glyph: 'squares',
  },
  /* NO "Orders" in the header. It lives in the account menu and, on mobile, in
     the Account section — which is also where production kept it: production's
     header is Cart / Dashboard / Marketplace and has never carried an Orders
     link. Orders is a place you go occasionally, from your account; the header
     is for the handful of destinations you move between constantly.

     `MY_ORDERS_TO` below is still the one definition of where orders live, so
     the account menu and the mobile Account row cannot drift apart.

     Dashboard IS here, and is `authOnly`: a guest has no dashboard, and a nav
     link whose only outcome is a redirect to /login is a dead end rather than
     navigation. */
  {
    id: 'dashboard',
    label: 'Dashboard',
    to: routes.DASHBOARD,
    match: '/dashboard',
    icon: Gauge,
    glyph: 'gauge',
    authOnly: true,
  },
];

/**
 * Where a buyer's own orders live. Not `/order-tracking`, which is a
 * track-by-reference lookup.
 *
 * It is the dashboard's order-history tab rather than a route of its own,
 * which is the pre-conversion arrangement: the dashboard has always been the
 * place you own things, and order history has always been a panel of it.
 */
export const MY_ORDERS_TO = `${routes.DASHBOARD}?tab=order_history`;

/**
 * MarketShell mobile tab bar. THREE destinations, and the shortness is the
 * point.
 *
 * It was five — Shop, Sets, Orders, Cart, Account — and three of those were
 * wrong:
 *
 *   Cart    pointed at `/checkout`, so tapping Cart SKIPPED the cart entirely
 *           and dropped the buyer into checkout. The cart is now the icon in
 *           the top right of the header, where it was before the conversion,
 *           and it opens the cart drawer rather than navigating at all.
 *   Orders  pointed at `/order-tracking`, a track-by-reference lookup, not the
 *           buyer's orders. It moved into the account section and now points
 *           at the order history that actually holds their orders.
 *   Sets    is the games index. See MARKET_NAV.
 *
 * What is left is the three things a buyer moves between: browse everything,
 * browse by game, and me. Everything else is one level in — which is the point
 * of an account section and the reason the bar does not need five slots.
 */
export const MARKET_TABS = [
  {
    id: 'marketplace',
    label: 'Shop',
    to: routes.MARKETPLACE,
    match: '/marketplace',
    alsoAt: [routes.HOME],
    glyph: 'storefront',
  },
  { id: 'sets', label: 'Games', to: routes.SETS, match: '/sets', glyph: 'squares' },
  /* Collection, and it is a TAB because below 1024 there was otherwise no way
     to reach a saved card mid-browse. The header heart lives in `TopNav`, which
     never mounts on this track, so the only entry point was Account -> Profile
     -> a row two taps deep (Profile.view.jsx). "I hearted this card, where did
     it go" has to have an answer that is visible from the page you hearted it
     on, which is what the bar is for.

     NOT `authOnly`, unlike Dashboard. `/collection` is deliberately open to
     guests -- a signed-out buyer's saves live on the device via
     `useFavourites`, and the page renders them -- so gating the only way in
     would hide a list from the very people who cannot get at it any other way.

     Placed before Dashboard so the buying tabs (Shop, Games, Collection) stay
     together and the two account-ish tabs close the row. */
  {
    id: 'collection',
    label: 'Collection',
    to: routes.COLLECTION,
    match: '/collection',
    glyph: 'heart',
    badge: 'wishlist',
  },
  /* Dashboard is a TAB for a signed in user, not a row buried two taps deep in
     Account. "I am in the marketplace, how do I get back to the dashboard" has
     to have the same answer on a phone as it does on a desktop, where it is a
     nav link. Signed out the bar is three tabs; signed in it is four, and the
     same `authOnly` flag drives both tracks so they cannot drift. */
  {
    id: 'dashboard',
    label: 'Dashboard',
    to: routes.DASHBOARD,
    match: '/dashboard',
    glyph: 'gauge',
    authOnly: true,
  },
  { id: 'account', label: 'Account', to: routes.PROFILE, match: '/profile', glyph: 'user' },
];

/**
 * The mobile surfaces whose top bar carries the SEARCH PILL instead of a title.
 *
 * Below 1024px the desktop `TopNav` never mounts, and it was the only thing in
 * the app holding the global search box — so search had no entry point at all
 * on a phone. The mobile header gets one, but it cannot get one everywhere: the
 * top bar has ONE centre slot, and on a card detail or a cart the thing in it
 * is the only label saying what page you are on.
 *
 * So the pill goes where the user's job IS finding a card, and nowhere else:
 *
 *   /            the front door — `App.js` mounts Marketplace at both paths
 *   /marketplace browse everything
 *   /sets        browse by game
 *   /search      the results page, where the box shows the query it answered
 *                and refining is editing what is already there
 *
 * DELIBERATELY EXACT, NOT A PREFIX. `/marketplace/card/:id` is a listing, not a
 * browse surface: it is a leaf you arrived at from a search, its title is the
 * card's name, and trading that for a second search box would take away the
 * one label telling you which card you are looking at. Same for the set drills
 * under `/sets/category/`. From any of them the Shop tab is one tap away.
 *
 * THAT ARGUMENT NO LONGER HOLDS, and this list is kept as it is only because
 * widening it is a product decision nobody has taken. Search stopped being a
 * field in the centre slot and became a 44px trigger in the end slot beside the
 * cart, so it no longer costs a leaf page its title — the reason every
 * exclusion above was written. If search should be on every market surface,
 * this Set is the only thing to change.
 */
const MOBILE_SEARCH_SURFACES = new Set([
  routes.HOME,
  routes.MARKETPLACE,
  routes.SETS,
  routes.SEARCH,
]);

/** `/marketplace/` and `/marketplace` are the same destination to a reader. */
export function isMobileSearchSurface(pathname) {
  if (!pathname) return false;
  const here = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  return MOBILE_SEARCH_SURFACES.has(here);
}

/**
 * ConsoleShell: the managing nav, seller identity.
 *
 * NOTHING RENDERS THIS ANY MORE and it is kept only as `ConsoleShell`'s
 * non-admin fallback. The seller's screens — dashboard, new listing, shipping
 * labels — moved onto `MarketShell` and into the primary nav, because a left
 * sidebar around them made the seller's own tools read as a separate
 * application rather than as part of Sleevr. `ConsoleShell` is now effectively
 * the ADMIN shell; see `ADMIN_NAV`, which is the only group set still in use.
 *
 * Do not reintroduce this as a seller sidebar without asking. It is a
 * deliberate information-architecture decision, not an oversight.
 */
export const CONSOLE_NAV = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        to: routes.DASHBOARD,
        match: '/dashboard',
        icon: Gauge,
        glyph: 'gauge',
      },
      /* NO "Trade desk". It pointed at `routes.SELLER_DASHBOARD`
         (`/seller-dashboard`), which is not a registered route in App.js and
         never has been — not in this branch and not in the pre-conversion app
         at d597edb1 — so the link fell through to the `*` route and served a
         404. The trade desk is a separate product from the marketplace and
         does not belong in this nav at all. */
    ],
  },
  {
    id: 'selling',
    label: 'Selling',
    items: [
      {
        id: 'create-listing',
        label: 'New listing',
        to: routes.CREATE_LISTING,
        match: '/create-listing',
        icon: Tag,
        glyph: 'tag',
      },
      /* NO "Orders" pointing at `/order-tracking`. It was the last non-admin
         entry point left into the track-by-reference lookup, and a seller has
         no tracking ID to type into it — the orders a seller manages are the
         table on their dashboard. Order tracking is an ADMIN tool and reaches
         its own console route below; see the note above MARKET_NAV. */
      {
        id: 'labels',
        label: 'Shipping labels',
        to: routes.CREATE_LABEL,
        match: '/create-label',
        icon: Truck,
        glyph: 'list',
      },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    items: [
      {
        id: 'profile',
        label: 'Profile',
        to: routes.PROFILE,
        match: '/profile',
        icon: Gear,
        glyph: 'gear',
      },
      {
        id: 'marketplace',
        label: 'Back to marketplace',
        to: routes.MARKETPLACE,
        match: '/marketplace',
        icon: Storefront,
        glyph: 'storefront',
      },
    ],
  },
];

/**
 * ConsoleShell, admin identity. Same component, different groups: the admin
 * routes are a ConsoleShell variant, never a second shell.
 */
export const ADMIN_NAV = [
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        id: 'admin-dashboard',
        label: 'Dashboard',
        to: '/admin-dashboard',
        match: '/admin-dashboard',
        icon: Gauge,
        glyph: 'gauge',
      },
      {
        id: 'admin-order-tracking',
        label: 'Order tracking',
        to: '/admin-order-tracking',
        match: '/admin-order-tracking',
        icon: Truck,
        glyph: 'list',
      },
      {
        id: 'admin-payouts',
        label: 'Payouts',
        to: '/admin-payouts',
        match: '/admin-payouts',
        icon: CurrencyCircleDollar,
        glyph: 'chart',
      },
    ],
  },
  {
    id: 'people',
    label: 'People',
    items: [
      {
        id: 'admin-sellers',
        label: 'Sellers',
        to: '/admin-sellers',
        match: '/admin-sellers',
        icon: Storefront,
        glyph: 'storefront',
      },
      {
        id: 'admin-buyers',
        label: 'Buyers',
        to: '/admin-buyers',
        match: '/admin-buyers',
        icon: UserCircle,
        glyph: 'user',
      },
      {
        id: 'admin-users',
        label: 'Users',
        to: '/admin-users',
        match: '/admin-users',
        icon: Users,
        glyph: 'user',
      },
    ],
  },
  {
    id: 'catalogue',
    label: 'Catalogue',
    items: [
      {
        id: 'admin-marketplace',
        label: 'Marketplace',
        to: routes.MARKETPLACE_DASHBOARD,
        match: '/market-dashboard',
        icon: House,
        glyph: 'squares',
      },
      {
        id: 'admin-settings',
        label: 'System settings',
        to: routes.ADMIN_SYSTEM_SETTINGS,
        match: '/admin-system-settings',
        icon: Gear,
        glyph: 'gear',
      },
    ],
  },
];

/**
 * Is `item` the current route?
 *
 * Prefix matching on `match` when it is given, exact otherwise, so
 * /marketplace/card/123 still lights up Marketplace while /profile does not
 * light up /profile-something-else.
 *
 * `alsoAt` is for the one thing prefixes cannot express: a SECOND path serving
 * the same page from somewhere else in the tree. Only `/` needs it today. It is
 * a list of exact paths and deliberately not a second prefix — the point is
 * that these paths share a destination, not a section.
 */
export function isNavItemActive(item, pathname) {
  if (!item || !pathname) return false;
  if (item.alsoAt?.includes(pathname)) return true;
  const prefix = item.match;
  if (!prefix) return pathname === item.to;
  if (pathname === prefix) return true;
  return pathname.startsWith(`${prefix}/`);
}
